import React, { useState, useEffect } from "react";
import { scoutApi, streamPost, emailApi, researchApi } from "../lib/api";
import { useDeal } from "../lib/store";
import { Card, ConfirmModal, ResearchModal, useResearch, MoneyInput } from "../components/shared";
import { fmt$, buyBoxScore } from "../lib/model";
import { toast } from "sonner";
import { Radar, ExternalLink, Plus, Trash2, Mail, Bookmark, X, Crosshair, Star, Save, Calculator, Sparkles, Phone, Link2, Search, Globe, UserSearch, Pencil, Check } from "lucide-react";

const SOURCES = ["BizBuySell", "LoopNet", "Axial", "BizQuest", "DealStream"];
const MARKETS = [
  "Dallas–Fort Worth, TX", "Fort Worth, TX", "Plano, TX", "Frisco, TX", "McKinney, TX", "Arlington, TX",
  "Irving, TX", "Denton, TX", "Garland, TX", "Richardson, TX",
  "Houston, TX", "San Antonio, TX", "Austin, TX", "El Paso, TX",
  "Atlanta, GA", "Charlotte, NC", "Raleigh, NC", "Nashville, TN", "Phoenix, AZ", "Denver, CO",
  "Las Vegas, NV", "Salt Lake City, UT", "Chicago, IL", "Columbus, OH", "Indianapolis, IN",
  "Kansas City, MO", "Minneapolis, MN", "Los Angeles, CA", "San Diego, CA", "Seattle, WA",
  "Portland, OR", "Miami, FL", "Tampa, FL", "Orlando, FL", "Jacksonville, FL",
  "New York, NY", "Boston, MA", "Philadelphia, PA", "Washington, DC", "Charleston, SC",
];
const STATUSES = ["Watching", "Reached Out", "NDA Requested", "NDA Signed", "In Diligence", "Passed"];
const SITE = { BizBuySell: "bizbuysell.com", LoopNet: "loopnet.com", Axial: "axial.net", BizQuest: "bizquest.com", DealStream: "dealstream.com" };

const KNOWN_BROKERAGES = [
  { k: "sunbelt", site: "sunbeltnetwork.com" }, { k: "transworld", site: "tworld.com" },
  { k: "murphy", site: "murphybusiness.com" }, { k: "vr ", site: "vrbb.com" },
  { k: "first choice", site: "fcbb.com" }, { k: "the firm", site: "thefirmadv.com" },
  { k: "calder", site: "calderassociates.com" }, { k: "raincatcher", site: "raincatcher.com" },
];

const brokerSearchLinks = (name, company, location) => {
  const g = (q) => `https://www.google.com/search?q=${encodeURIComponent(q)}`;
  const nm = (name || "").trim(), co = (company || "").trim(), loc = (location || "").trim();
  const links = [];
  if (nm) {
    links.push({ label: "Google — broker profile", url: g(`"${nm}" ${co} business broker`) });
    links.push({ label: "Google — phone & email", url: g(`"${nm}" ${co} contact phone email`) });
    links.push({ label: "LinkedIn profile", url: g(`site:linkedin.com "${nm}" ${co}`) });
  }
  const known = co && KNOWN_BROKERAGES.find((b) => co.toLowerCase().includes(b.k));
  if (known) links.push({ label: `${co} directory (${known.site})`, url: g(`site:${known.site} ${nm || loc}`) });
  if (co) links.push({ label: "Brokerage office page", url: g(`${co} business brokers ${loc} office contact`) });
  links.push({ label: "Google Maps — office line", url: `https://www.google.com/maps/search/${encodeURIComponent(`${co || "business broker"} ${loc}`.trim())}` });
  return links;
};

const dealLink = (source, businessType, location) => {
  const q = encodeURIComponent(`${businessType || ""} ${location || ""} for sale`.trim());
  const site = SITE[source];
  return site ? `https://www.google.com/search?q=site:${site}+${q}` : `https://www.google.com/search?q=${q}`;
};

const parseListings = (full) => {
  const clean = (full || "").replace(/```json/gi, "").replace(/```/g, "").trim();
  try {
    const s = clean.indexOf("{"), e = clean.lastIndexOf("}");
    const arr = JSON.parse(clean.slice(s, e + 1)).listings;
    if (Array.isArray(arr) && arr.length) return arr;
  } catch (_) {}
  const out = [];
  const re = /\{[^{}]*\}/g; let mm;
  while ((mm = re.exec(clean))) { try { const o = JSON.parse(mm[0]); if (o && (o.codeName || o.businessType)) out.push(o); } catch (_) {} }
  return out;
};

const num = (x) => (x === "" || x === null || x === undefined || isNaN(Number(x)) ? null : Number(x));
const emptyDeal = { name: "", businessType: "", source: "Broker Direct", broker: "", brokerCompany: "", brokerEmail: "", brokerPhone: "", location: "", revenue: "", ebitda: "", askingPrice: "", summary: "" };
const emptyPaste = { url: "", name: "", businessType: "", source: "", broker: "", brokerCompany: "", brokerPhone: "", brokerEmail: "", location: "", summary: "", cimReceived: false };

const BuyBox = ({ bb }) => {
  if (!bb) return <span className="pill" style={{ fontSize: 9.5, opacity: 0.65 }} title="Enter asking price + EBITDA to score against your model">buy-box n/a</span>;
  const tone = bb.clears ? "go" : bb.score >= 45 ? "amber" : "pass";
  const tip = `vs your model — DSCR ${bb.dscr.toFixed(2)}× · IRR ${bb.irr.toFixed(0)}% · MOIC ${bb.moic.toFixed(2)}× · ${bb.multiple.toFixed(1)}× SDE`;
  return <span className={`pill ${tone}`} style={{ fontSize: 9.5 }} title={tip}>{bb.clears ? "✓ clears " : "buy-box "}{bb.score}</span>;
};

export default function DealScout() {
  const { M, patch, setDealName, navigate, industry: dealIndustry } = useDeal();
  const [filters, setFilters] = useState({ sector: dealIndustry, ebitdaMin: "", ebitdaMax: "", priceMin: "", priceMax: "" });
  useEffect(() => { setFilters((f) => ({ ...f, sector: dealIndustry })); }, [dealIndustry]);
  const [marketSel, setMarketSel] = useState(["Dallas–Fort Worth, TX"]);
  const [cityQuery, setCityQuery] = useState("");
  const [showCitySug, setShowCitySug] = useState(false);
  const [srcSel, setSrcSel] = useState(() => Object.fromEntries(SOURCES.map((s) => [s, true])));
  const [listings, setListings] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [tracked, setTracked] = useState([]);
  const [form, setForm] = useState(emptyDeal);
  const [showAdd, setShowAdd] = useState(false);
  const [searches, setSearches] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [searchName, setSearchName] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [paste, setPaste] = useState(emptyPaste);
  const [parsing, setParsing] = useState(false);
  const [contactSearch, setContactSearch] = useState(null);
  const [research, setResearch] = useState(null);
  const [researching, setResearching] = useState(false);
  const [apollo, setApollo] = useState(null);
  const [apolloBusy, setApolloBusy] = useState(false);
  const [trackerFilter, setTrackerFilter] = useState("all");
  const [editId, setEditId] = useState(null);
  const [editRow, setEditRow] = useState({});
  const shownTracked = tracked.filter((d) => trackerFilter === "all" ? true : trackerFilter === "watch" ? d.source === "Closed-deal intel" : d.source !== "Closed-deal intel");
  const watchCount = tracked.filter((d) => d.source === "Closed-deal intel").length;

  const loadTracked = async () => setTracked(await scoutApi.list());
  const loadSearches = async () => setSearches(await scoutApi.listSearches());
  useEffect(() => { loadTracked(); loadSearches(); }, []);

  const trackedNames = new Set(tracked.map((d) => (d.name || "").toLowerCase().trim()));

  const marketStr = () => (marketSel.length === 0 ? "Nationwide, United States" : marketSel.join("; "));
  const addCity = (c) => { const v = (c || "").trim(); if (v && !marketSel.includes(v)) setMarketSel((s) => [...s, v]); setCityQuery(""); };
  const removeCity = (c) => setMarketSel((s) => s.filter((x) => x !== c));
  const citySug = ["Nationwide, United States", ...MARKETS].filter((c) => c.toLowerCase().includes(cityQuery.toLowerCase()) && !marketSel.includes(c)).slice(0, 8);

  const scout = async (ov = null) => {
    setScanning(true); setListings([]);
    try {
      const payload = ov ? {
        sector: ov.sector || "", market: (ov.markets && ov.markets.length ? ov.markets.join("; ") : "Nationwide, United States"),
        ebitdaMin: num(ov.ebitdaMin), ebitdaMax: num(ov.ebitdaMax), priceMin: num(ov.priceMin), priceMax: num(ov.priceMax),
        sources: (ov.sources && ov.sources.length ? ov.sources : SOURCES),
      } : {
        sector: filters.sector, market: marketStr(),
        ebitdaMin: num(filters.ebitdaMin), ebitdaMax: num(filters.ebitdaMax),
        priceMin: num(filters.priceMin), priceMax: num(filters.priceMax),
        sources: SOURCES.filter((s) => srcSel[s]),
      };
      let full = "";
      await streamPost("/ai/scout", payload, (c, f) => { full = f; });
      const arr = parseListings(full).sort((a, b) => (b.fitScore || 0) - (a.fitScore || 0));
      setListings(arr);
      if (arr.length) toast.success(`Surfaced ${arr.length} candidate deals`);
      else toast.error("No deals surfaced — adjust filters and retry");
    } catch { toast.error("Deal scout failed — try again"); }
    finally { setScanning(false); }
  };

  const openSaveSearch = () => {
    setSearchName(filters.sector ? `${filters.sector} — ${marketSel[0] || "Nationwide"}` : "My search");
    setSaveOpen(true);
  };
  const confirmSaveSearch = async () => {
    const name = searchName.trim();
    if (!name) return toast.error("Enter a name for this search");
    const created = await scoutApi.createSearch({
      name, sector: filters.sector, markets: marketSel,
      ebitdaMin: num(filters.ebitdaMin), ebitdaMax: num(filters.ebitdaMax),
      priceMin: num(filters.priceMin), priceMax: num(filters.priceMax),
      sources: SOURCES.filter((s) => srcSel[s]),
    });
    setSearches((s) => [created, ...s]);
    setSaveOpen(false);
    toast.success(`Saved “${created.name}”`);
  };
  const applySearch = (s) => {
    setFilters({ sector: s.sector || "", ebitdaMin: s.ebitdaMin ?? "", ebitdaMax: s.ebitdaMax ?? "", priceMin: s.priceMin ?? "", priceMax: s.priceMax ?? "" });
    setMarketSel(s.markets || []);
    setSrcSel(Object.fromEntries(SOURCES.map((x) => [x, !(s.sources || []).length || s.sources.includes(x)])));
    toast.info(`Running “${s.name}”`);
    scout({ sector: s.sector, markets: s.markets, ebitdaMin: s.ebitdaMin, ebitdaMax: s.ebitdaMax, priceMin: s.priceMin, priceMax: s.priceMax, sources: s.sources });
  };
  const deleteSearch = (s) => setConfirm({ title: "Delete saved search?", message: `“${s.name}” will be removed.`, confirmLabel: "Delete", danger: true, onConfirm: async () => { await scoutApi.removeSearch(s.id); setSearches((x) => x.filter((y) => y.id !== s.id)); setConfirm(null); } });

  const underwrite = (l) => {
    const name = l.codeName || l.name || M.dealName;
    const price = num(l.askingPrice), sde = num(l.ebitda), rev = num(l.revenue);
    patch({
      dealName: name,
      companyName: l.businessType || l.name || M.companyName,
      address: l.location || M.address,
      brokerName: l.broker || M.brokerName,
      ...(price ? { askPrice: price, goodwill: Math.max(0, price - (M.ffe || 0) - (M.nwc || 0)) } : {}),
      ...(sde ? { sde } : {}),
      ...(rev ? { ttmRevenue: rev } : {}),
    });
    setDealName(name);
    toast.success(`Loaded ${name} into the model — running returns`);
    navigate("returns");
  };

  const track = async (l) => {
    const created = await scoutApi.create({
      name: l.codeName || l.businessType || "Untitled deal",
      businessType: l.businessType || "", source: l.source || "", sourceUrl: dealLink(l.source, l.businessType, l.location),
      broker: l.broker || "", brokerContact: l.brokerContact || "", location: l.location || "",
      revenue: num(l.revenue), ebitda: num(l.ebitda), askingPrice: num(l.askingPrice),
      summary: l.summary || "", status: "Watching",
    });
    setTracked((t) => [created, ...t]);
    toast.success(`Tracking ${created.name}`);
  };

  const addManual = async () => {
    if (!form.name) return toast.error("Deal name required");
    const created = await scoutApi.create({
      ...form, revenue: num(form.revenue), ebitda: num(form.ebitda), askingPrice: num(form.askingPrice),
      sourceUrl: form.source && SITE[form.source] ? dealLink(form.source, form.businessType, form.location) : "",
      status: "Watching",
    });
    setTracked((t) => [created, ...t]); setForm(emptyDeal); setShowAdd(false);
    toast.success(`Added ${created.name} to the tracker`);
  };

  const setStatus = async (id, status) => {
    const upd = await scoutApi.update(id, { status });
    setTracked((t) => t.map((d) => (d.id === id ? upd : d)));
  };
  const setNotes = async (id, notes) => setTracked((t) => t.map((d) => (d.id === id ? { ...d, notes } : d)));
  const saveNotes = async (id, notes) => { await scoutApi.update(id, { notes }); };

  const startEdit = (d) => {
    setEditId(d.id);
    setEditRow({
      name: d.name || "", businessType: d.businessType || "", location: d.location || "",
      source: d.source || "", sourceUrl: d.sourceUrl || "",
      revenue: d.revenue ?? "", ebitda: d.ebitda ?? "", askingPrice: d.askingPrice ?? "",
      broker: d.broker || "", brokerCompany: d.brokerCompany || "", brokerEmail: d.brokerEmail || "", brokerPhone: d.brokerPhone || "",
      summary: d.summary || "", status: d.status || "Watching", notes: d.notes || "",
    });
  };
  const cancelEdit = () => { setEditId(null); setEditRow({}); };
  const ef = (k, v) => setEditRow((r) => ({ ...r, [k]: v }));
  const saveEdit = async () => {
    if (!editRow.name || !editRow.name.trim()) return toast.error("Deal name is required");
    const payload = {
      ...editRow, name: editRow.name.trim(),
      revenue: num(editRow.revenue), ebitda: num(editRow.ebitda), askingPrice: num(editRow.askingPrice),
    };
    try {
      const upd = await scoutApi.update(editId, payload);
      setTracked((t) => t.map((d) => (d.id === editId ? upd : d)));
      cancelEdit();
      toast.success("Deal updated");
    } catch { toast.error("Could not save changes — try again"); }
  };

  const remove = (d) => setConfirm({ title: "Remove tracked deal?", message: `“${d.name}” will be removed from your tracker.`, confirmLabel: "Remove", danger: true, onConfirm: async () => { await scoutApi.remove(d.id); setTracked((t) => t.filter((x) => x.id !== d.id)); setConfirm(null); } });

  const autofillPaste = async () => {
    if (!paste.url.trim()) return toast.error("Paste a listing URL first");
    setParsing(true);
    try {
      const d = await scoutApi.parseUrl(paste.url.trim());
      setPaste((p) => ({ ...p, name: d.name || p.name, businessType: d.businessType || p.businessType, source: d.source || p.source, location: d.location || p.location, summary: d.summary || p.summary }));
      toast.success("Auto-filled from URL — add the broker name & number");
    } catch { toast.error("Could not read that URL"); }
    finally { setParsing(false); }
  };
  const savePaste = async () => {
    if (!paste.url.trim() && !paste.name.trim()) return toast.error("Paste a URL or enter a deal name");
    const created = await scoutApi.create({
      name: paste.name || paste.businessType || "Listing", businessType: paste.businessType,
      source: paste.source || "Broker Direct", sourceUrl: paste.url.trim(), broker: paste.broker,
      brokerCompany: paste.brokerCompany, brokerPhone: paste.brokerPhone, brokerEmail: paste.brokerEmail,
      location: paste.location, summary: paste.summary, cimReceived: paste.cimReceived, status: "Watching",
    });
    setTracked((t) => [created, ...t]);
    setPaste(emptyPaste);
    toast.success(`Saved ${created.name} — follow up with the broker for the CIM`);
  };
  const toggleCim = async (d) => {
    const upd = await scoutApi.update(d.id, { cimReceived: !d.cimReceived });
    setTracked((t) => t.map((x) => (x.id === d.id ? upd : x)));
  };

  const openContact = (payload) => { setResearch(null); setApollo(null); setContactSearch(payload); };
  const runApollo = async () => {
    setApolloBusy(true);
    try {
      const r = await researchApi.apollo({ name: contactSearch.broker, company: contactSearch.company });
      setApollo(r);
      const hit = r.found && (r.email || r.phone);
      toast[hit ? "success" : "message"](hit ? "Apollo found contact details" : "Apollo matched the person but no unlocked email/phone (needs credits)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Apollo lookup failed"); }
    finally { setApolloBusy(false); }
  };
  const runResearch = async () => {
    setResearching(true);
    try {
      const r = await scoutApi.researchBroker({ broker: contactSearch.broker, name: contactSearch.broker, company: contactSearch.company, location: contactSearch.location });
      setResearch(r);
      const found = [r.office_phone, r.business_email].filter(Boolean).length;
      toast[found ? "success" : "message"](found ? `Found ${found} contact detail(s) — verify via sources` : "No direct contact found — see profile sources below");
    } catch (e) { toast.error(e?.response?.data?.detail || "Research failed"); }
    finally { setResearching(false); }
  };
  const applyContact = async (field, val) => {
    if (!val) return;
    if (contactSearch.dealId) {
      const upd = await scoutApi.update(contactSearch.dealId, { [field]: val });
      setTracked((t) => t.map((x) => (x.id === upd.id ? upd : x)));
    } else {
      setPaste((p) => ({ ...p, [field]: val }));
    }
    toast.success("Added — verify against the source before outreach");
  };

  const draftNda = async (d) => {
    if (!d.brokerEmail) return toast.error("Add the broker's email first (edit the deal)");
    const subject = `NDA & information request — ${d.name}`;
    const body = `Hi ${d.brokerContact || d.broker || "there"},\n\nXYZ Ventures is a qualified acquirer actively reviewing opportunities in ${d.businessType || "this space"}${d.location ? ` (${d.location})` : ""}. We're interested in ${d.name} and would like to move forward.\n\nCould you please send the NDA so we can review the full CIM and financials? We're prepared to sign promptly and can share proof of funds on request.\n\nBest regards,\nXYZ Ventures`;
    try {
      const res = await emailApi.compose({ to: [{ name: d.broker, email: d.brokerEmail }], subject, body, deal_name: d.name, kind: "scout" });
      if (res.mock) toast.warning("Recorded — no live email (sender config)");
      else toast.success(`NDA request sent to ${d.brokerEmail}`);
      setStatus(d.id, "NDA Requested");
    } catch { toast.error("Send failed"); }
  };

  const statusTone = (s) => (s === "NDA Signed" || s === "In Diligence" ? "go" : s === "Passed" ? "pass" : "amber");

  const listingResearch = useResearch("listings");
  const S = (v) => (Array.isArray(v) ? v.join(", ") : v == null ? "" : String(v));
  const saveListing = async (e) => {
    const created = await scoutApi.create({
      name: S(e.name) || S(e.businessType) || "Listing", businessType: S(e.businessType), source: S(e.source) || "Web",
      sourceUrl: S(e.url), broker: S(e.broker), brokerCompany: S(e.brokerCompany), location: S(e.location),
      summary: S(e.summary), status: "Watching",
    });
    setTracked((t) => [created, ...t]);
    toast.success(`Tracking ${created.name}`);
  };
  const describeListing = (e) => (
    <div style={{ fontSize: 12 }}>
      <div><b>{e.name}</b>{e.source ? <span className="tag" style={{ marginLeft: 6, fontSize: 9 }}>{e.source}</span> : null}</div>
      <div style={{ color: "var(--text2)", marginTop: 2, fontSize: 11 }}>{[e.businessType, e.location].filter(Boolean).join(" · ")}</div>
      {e.summary && <div style={{ color: "var(--text3)", marginTop: 2 }}>{e.summary}</div>}
      <div style={{ color: "var(--text3)", marginTop: 2, fontSize: 11 }}>{[e.broker, e.brokerCompany].filter(Boolean).join(" · ")}</div>
      {e.url && <a href={e.url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: "var(--lime)" }}>view listing</a>}
    </div>
  );

  return (
    <div className="page" data-testid="page-scout">
      <div className="page-head">
        <div className="page-title">Deal Scout</div>
        <div className="page-sub">Surface on-market acquisition targets, click through to live listings, and track every deal you're chasing</div>
      </div>

      <div className="banner info" data-testid="scout-disclaimer"><span>ℹ</span><span>AI-surfaced leads modeled on typical on-market listings — <b>verify each on the source site</b> via the View / Request Info link before outreach.</span></div>

      <Card title="Paste a Listing — Broker Follow-up" testid="scout-paste"
        right={<button className="btn btn-sm" onClick={autofillPaste} disabled={parsing} data-testid="paste-autofill"><Sparkles size={12} />{parsing ? "Reading…" : "Auto-fill from URL"}</button>}>
        <div className="bullet">Paste a listing link, capture the broker's name & number and a quick summary, and save it here — so you can follow up for the CIM even before it arrives.</div>
        <div className="field"><label>Listing URL</label>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <Link2 size={14} style={{ color: "var(--text3)" }} />
            <input className="inp" style={{ flex: 1 }} placeholder="https://www.loopnet.com/biz/business-opportunity/…" value={paste.url} onChange={(e) => setPaste({ ...paste, url: e.target.value })} data-testid="paste-url" />
          </div>
        </div>
        <div className="grid g3">
          <div className="field"><label>Deal / business name</label><input className="inp" value={paste.name} onChange={(e) => setPaste({ ...paste, name: e.target.value })} data-testid="paste-name" /></div>
          <div className="field"><label>Business type</label><input className="inp" value={paste.businessType} onChange={(e) => setPaste({ ...paste, businessType: e.target.value })} data-testid="paste-type" /></div>
          <div className="field"><label>Location</label><input className="inp" value={paste.location} onChange={(e) => setPaste({ ...paste, location: e.target.value })} data-testid="paste-location" /></div>
          <div className="field"><label>Broker name</label><input className="inp" value={paste.broker} onChange={(e) => setPaste({ ...paste, broker: e.target.value })} data-testid="paste-broker" /></div>
          <div className="field"><label>Brokerage / company</label><input className="inp" placeholder="e.g. Sunbelt" value={paste.brokerCompany} onChange={(e) => setPaste({ ...paste, brokerCompany: e.target.value })} data-testid="paste-company" /></div>
          <div className="field"><label>Broker phone</label><input className="inp" placeholder="(xxx) xxx-xxxx" value={paste.brokerPhone} onChange={(e) => setPaste({ ...paste, brokerPhone: e.target.value })} data-testid="paste-phone" /></div>
          <div className="field"><label>Broker email</label>
            <div style={{ display: "flex", gap: 6 }}>
              <input className="inp" style={{ flex: 1 }} value={paste.brokerEmail} onChange={(e) => setPaste({ ...paste, brokerEmail: e.target.value })} data-testid="paste-email" />
              <button type="button" className="btn" style={{ whiteSpace: "nowrap" }} onClick={() => openContact({ broker: paste.broker, company: paste.brokerCompany, location: paste.location })} data-testid="paste-find-contact"><Search size={13} />Find broker contact</button>
            </div>
          </div>
        </div>
        <div className="field"><label>Deal summary</label><textarea className="inp" rows={2} value={paste.summary} onChange={(e) => setPaste({ ...paste, summary: e.target.value })} data-testid="paste-summary" /></div>
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 4 }}>
          <label style={{ display: "flex", gap: 6, fontSize: 11.5, alignItems: "center", cursor: "pointer" }}>
            <input type="checkbox" checked={paste.cimReceived} onChange={(e) => setPaste({ ...paste, cimReceived: e.target.checked })} data-testid="paste-cim" /> CIM received
          </label>
          <span style={{ flex: 1 }} />
          <button className="btn btn-primary" onClick={savePaste} data-testid="paste-save"><Bookmark size={13} />Save to Tracker</button>
        </div>
      </Card>

      <Card title="Find Deals" testid="scout-filters" right={<div style={{ display: "flex", gap: 6 }}>
          <button className="btn" onClick={() => listingResearch.start(filters.sector || "small business for sale", marketSel[0] || "United States")} data-testid="scout-web-listings"><Globe size={13} style={{ marginRight: 4 }} />Find real listings (web)</button>
          <button className="btn" onClick={openSaveSearch} data-testid="save-search"><Save size={13} style={{ marginRight: 4 }} />Save search</button>
          <button className="btn btn-primary" onClick={() => scout()} disabled={scanning} data-testid="scout-run"><Radar size={13} style={{ marginRight: 4 }} />{scanning ? "Scanning…" : "Scout Deals"}</button>
        </div>}>
        <div>
          {searches.length > 0 && (
            <div className="field">
              <label>Saved searches — one-click re-run</label>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }} data-testid="saved-searches">
                {searches.map((s) => (
                  <span key={s.id} className="saved-chip" data-testid={`saved-${s.id}`}>
                    <button onClick={() => applySearch(s)} title="Load & run" data-testid={`saved-run-${s.id}`}><Star size={11} style={{ marginRight: 5 }} />{s.name}</button>
                    <button className="x" onClick={() => deleteSearch(s)} title="Delete" data-testid={`saved-del-${s.id}`}><X size={11} /></button>
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="field"><label>Sector / keyword</label><input className="inp" value={filters.sector} onChange={(e) => setFilters({ ...filters, sector: e.target.value })} data-testid="scout-sector" /></div>
        <div className="field">
          <label>Markets — search &amp; select cities</label>
          <div className="city-combo" data-testid="scout-market-combo">
            {marketSel.map((c) => (
              <span key={c} className="city-chip" data-testid={`city-chip-${c.replace(/[^a-z0-9]/gi, "-")}`}>
                {c}
                <button onClick={() => removeCity(c)} data-testid={`city-remove-${c.replace(/[^a-z0-9]/gi, "-")}`}><X size={12} /></button>
              </span>
            ))}
            <div className="city-input-wrap">
              <Crosshair size={14} className="city-ico" />
              <input
                className="city-input"
                placeholder={marketSel.length ? "Add another city, county or state" : "Search any city, county or state"}
                value={cityQuery}
                onChange={(e) => { setCityQuery(e.target.value); setShowCitySug(true); }}
                onFocus={() => setShowCitySug(true)}
                onBlur={() => setTimeout(() => setShowCitySug(false), 150)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCity(citySug[0] || cityQuery); } }}
                data-testid="scout-city-search"
              />
              {showCitySug && (citySug.length > 0 || cityQuery.trim()) && (
                <div className="city-sug" data-testid="city-suggestions">
                  {citySug.map((c) => (
                    <div key={c} className="city-sug-item" onMouseDown={() => addCity(c)} data-testid={`city-sug-${c.replace(/[^a-z0-9]/gi, "-")}`}>{c}</div>
                  ))}
                  {cityQuery.trim() && !citySug.some((c) => c.toLowerCase() === cityQuery.trim().toLowerCase()) && (
                    <div className="city-sug-item add" onMouseDown={() => addCity(cityQuery)} data-testid="city-sug-add">Add “{cityQuery.trim()}”</div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div style={{ fontSize: 10.5, color: "var(--text3)", marginTop: 4 }}>{marketSel.length === 0 ? "No market selected — searching Nationwide" : `${marketSel.length} market${marketSel.length === 1 ? "" : "s"} selected`}</div>
        </div>
        </div>
        <div className="grid g4">
          <div className="field"><label>EBITDA/SDE min ($)</label><MoneyInput value={filters.ebitdaMin} onChange={(v) => setFilters({ ...filters, ebitdaMin: v })} testid="scout-ebitda-min" /></div>
          <div className="field"><label>EBITDA/SDE max ($)</label><MoneyInput value={filters.ebitdaMax} onChange={(v) => setFilters({ ...filters, ebitdaMax: v })} testid="scout-ebitda-max" /></div>
          <div className="field"><label>Price min ($)</label><MoneyInput value={filters.priceMin} onChange={(v) => setFilters({ ...filters, priceMin: v })} testid="scout-price-min" /></div>
          <div className="field"><label>Price max ($)</label><MoneyInput value={filters.priceMax} onChange={(v) => setFilters({ ...filters, priceMax: v })} testid="scout-price-max" /></div>
        </div>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 4 }}>
          <span style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text2)" }}>Sources</span>
          {SOURCES.map((s) => (
            <label key={s} style={{ display: "flex", gap: 6, fontSize: 11.5, alignItems: "center", cursor: "pointer" }}>
              <input type="checkbox" checked={!!srcSel[s]} onChange={(e) => setSrcSel({ ...srcSel, [s]: e.target.checked })} data-testid={`scout-src-${s}`} /> {s}
            </label>
          ))}
        </div>
      </Card>

      {(scanning || listings.length > 0) && (
        <Card title={`Candidate Deals${listings.length ? ` (${listings.length})` : ""}`} style={{ marginTop: 12 }} testid="scout-results">
          {scanning && <div className="bullet">Scanning BizBuySell, LoopNet, Axial and broker networks for matches…</div>}
          <div className="grid g2">
            {listings.map((l, i) => {
              const isTracked = trackedNames.has((l.codeName || "").toLowerCase().trim());
              const bb = buyBoxScore(l, M);
              return (
                <div key={i} className="card" style={{ background: "var(--bg2)", border: "1px solid var(--border)" }} data-testid="scout-card">
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{l.codeName}</div>
                      <div style={{ fontSize: 11.5, color: "var(--text2)" }}>{l.businessType} · {l.location}</div>
                    </div>
                    <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
                      <span className="tag">{l.source}</span>
                      {typeof l.fitScore === "number" && <span className={`pill ${l.fitScore >= 75 ? "go" : l.fitScore >= 50 ? "amber" : "pass"}`}>{l.fitScore} fit</span>}
                      <BuyBox bb={bb} />
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 12px", fontSize: 11.5, marginTop: 8 }}>
                    <div><span style={{ color: "var(--text3)" }}>Revenue: </span>{l.revenue ? fmt$(l.revenue) : "—"}</div>
                    <div><span style={{ color: "var(--text3)" }}>EBITDA/SDE: </span>{l.ebitda ? fmt$(l.ebitda) : "—"}</div>
                    <div><span style={{ color: "var(--text3)" }}>Asking: </span>{l.askingPrice ? fmt$(l.askingPrice) : "—"}</div>
                    <div><span style={{ color: "var(--text3)" }}>Multiple: </span>{l.multiple || "—"}</div>
                  </div>
                  <div className="bullet" style={{ marginTop: 6 }}>{l.summary}</div>
                  {l.highlights && <div style={{ fontSize: 11.5, color: "var(--text2)", marginTop: 4 }}><b style={{ color: "var(--text)" }}>Highlights:</b> {l.highlights}</div>}
                  <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 4 }}>Broker: {l.broker || "—"}{l.brokerContact ? ` · ${l.brokerContact}` : ""}</div>
                  <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                    <a className="btn btn-primary" href={dealLink(l.source, l.businessType, l.location)} target="_blank" rel="noreferrer" data-testid={`scout-view-${i}`}><ExternalLink size={13} style={{ marginRight: 4 }} />View / Request Info</a>
                    <button className="btn" onClick={() => underwrite(l)} data-testid={`scout-underwrite-${i}`}><Calculator size={13} style={{ marginRight: 4 }} />Underwrite</button>
                    <button className="btn" onClick={() => track(l)} disabled={isTracked} data-testid={`scout-track-${i}`}><Bookmark size={13} style={{ marginRight: 4 }} />{isTracked ? "Tracking" : "Track Deal"}</button>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <Card title={`Deal Tracker (${shownTracked.length})`} style={{ marginTop: 12 }} className="table-scroll" testid="scout-tracker"
        right={<div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <div className="scen-bar" data-testid="tracker-filter">
            <button className={trackerFilter === "all" ? "active" : ""} onClick={() => setTrackerFilter("all")} data-testid="tracker-filter-all">All ({tracked.length})</button>
            <button className={trackerFilter === "live" ? "active" : ""} onClick={() => setTrackerFilter("live")} data-testid="tracker-filter-live">Live deals ({tracked.length - watchCount})</button>
            <button className={trackerFilter === "watch" ? "active" : ""} onClick={() => setTrackerFilter("watch")} data-testid="tracker-filter-watch">Watchlist ({watchCount})</button>
          </div>
          <button className="btn" onClick={() => setShowAdd((s) => !s)} data-testid="scout-add-toggle"><Plus size={13} style={{ marginRight: 4 }} />Add deal manually</button>
        </div>}>
        {showAdd && (
          <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 3, padding: 12, marginBottom: 12 }} data-testid="scout-add-form">
            <div className="grid g3">
              <div className="field"><label>Deal name *</label><input className="inp" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="add-name" /></div>
              <div className="field"><label>Business type</label><input className="inp" value={form.businessType} onChange={(e) => setForm({ ...form, businessType: e.target.value })} data-testid="add-type" /></div>
              <div className="field"><label>Source</label><input className="inp" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} data-testid="add-source" /></div>
              <div className="field"><label>Broker</label><input className="inp" value={form.broker} onChange={(e) => setForm({ ...form, broker: e.target.value })} data-testid="add-broker" /></div>
              <div className="field"><label>Brokerage / company</label><input className="inp" value={form.brokerCompany} onChange={(e) => setForm({ ...form, brokerCompany: e.target.value })} data-testid="add-company" /></div>
              <div className="field"><label>Broker email</label><input className="inp" value={form.brokerEmail} onChange={(e) => setForm({ ...form, brokerEmail: e.target.value })} data-testid="add-broker-email" /></div>
              <div className="field"><label>Broker phone</label><input className="inp" value={form.brokerPhone} onChange={(e) => setForm({ ...form, brokerPhone: e.target.value })} data-testid="add-broker-phone" /></div>
              <div className="field"><label>Location</label><input className="inp" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="add-location" /></div>
              <div className="field"><label>Revenue ($)</label><MoneyInput value={form.revenue} onChange={(v) => setForm({ ...form, revenue: v })} testid="add-revenue" /></div>
              <div className="field"><label>EBITDA/SDE ($)</label><MoneyInput value={form.ebitda} onChange={(v) => setForm({ ...form, ebitda: v })} testid="add-ebitda" /></div>
              <div className="field"><label>Asking price ($)</label><MoneyInput value={form.askingPrice} onChange={(v) => setForm({ ...form, askingPrice: v })} testid="add-price" /></div>
            </div>
            <div className="field"><label>Summary</label><input className="inp" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} data-testid="add-summary" /></div>
            <button className="btn btn-primary" onClick={addManual} data-testid="add-submit">Add to Tracker</button>
          </div>
        )}
        {shownTracked.length === 0 ? <div style={{ fontSize: 12, color: "var(--text3)" }}>No deals in this view yet. Scout deals above, add one manually, or Watch an acquirer from Closed Deals.</div> : (
          <table className="tbl tracker-tbl">
            <thead><tr><th>Deal</th><th>Source</th><th className="num">EBITDA</th><th className="num">Asking</th><th>Broker</th><th>Status</th><th>Notes</th><th style={{ textAlign: "right" }}>Actions</th></tr></thead>
            <tbody>
              {shownTracked.map((d) => (
                <React.Fragment key={d.id}>
                <tr data-testid="tracker-row" className={editId === d.id ? "hl" : ""} style={{ verticalAlign: "top" }}>
                  <td>
                    <b>{d.name}</b><div style={{ fontSize: 10.5, color: "var(--text3)" }}>{[d.businessType, d.location].filter(Boolean).join(" · ") || "—"}</div>
                    {d.sourceUrl && <a href={d.sourceUrl} target="_blank" rel="noreferrer" style={{ fontSize: 10.5, color: "var(--lime)" }}>view listing</a>}
                  </td>
                  <td>{d.source || "—"}</td>
                  <td className="num">{d.ebitda ? fmt$(d.ebitda) : <button className="cell-add" onClick={() => startEdit(d)} data-testid={`tracker-add-ebitda-${d.id}`} title="Add EBITDA">+ add</button>}</td>
                  <td className="num">{d.askingPrice ? fmt$(d.askingPrice) : <button className="cell-add" onClick={() => startEdit(d)} data-testid={`tracker-add-asking-${d.id}`} title="Add asking price">+ add</button>}</td>
                  <td><BuyBox bb={buyBoxScore(d, M)} /></td>
                  <td style={{ fontSize: 11 }}>
                    {d.broker || <button className="cell-add" onClick={() => startEdit(d)} data-testid={`tracker-add-broker-${d.id}`} title="Add broker">+ add</button>}
                    {d.brokerCompany && <div style={{ color: "var(--text2)" }}>{d.brokerCompany}</div>}
                    {d.brokerPhone && <div style={{ color: "var(--text2)", display: "flex", alignItems: "center", gap: 4 }}><Phone size={10} />{d.brokerPhone}</div>}
                    {d.brokerEmail && <div style={{ color: "var(--text3)", wordBreak: "break-all" }}>{d.brokerEmail}</div>}
                    <button className={`pill ${d.cimReceived ? "go" : "amber"}`} style={{ marginTop: 4, fontSize: 9, cursor: "pointer" }} onClick={() => toggleCim(d)} title="Toggle CIM received" data-testid={`tracker-cim-${d.id}`}>{d.cimReceived ? "CIM ✓" : "CIM pending"}</button>
                  </td>
                  <td>
                    <select className="inp" style={{ width: 130, padding: "4px 6px", fontSize: 11 }} value={d.status} onChange={(e) => setStatus(d.id, e.target.value)} data-testid={`tracker-status-${d.id}`}>
                      {STATUSES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                    <span className={`pill ${statusTone(d.status)}`} style={{ marginTop: 4, display: "inline-block", fontSize: 9.5 }}>{d.status}</span>
                  </td>
                  <td><input className="inp" style={{ width: 150, padding: "4px 6px", fontSize: 11 }} value={d.notes || ""} onChange={(e) => setNotes(d.id, e.target.value)} onBlur={(e) => saveNotes(d.id, e.target.value)} placeholder="notes…" data-testid={`tracker-notes-${d.id}`} /></td>
                  <td>
                    <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                      <button className={`btn ${editId === d.id ? "btn-primary" : ""}`} style={{ padding: "3px 6px" }} onClick={() => (editId === d.id ? cancelEdit() : startEdit(d))} title="Edit deal" data-testid={`tracker-edit-${d.id}`}><Pencil size={13} /></button>
                      <button className="btn" style={{ padding: "3px 6px" }} onClick={() => underwrite({ codeName: d.name, businessType: d.businessType, location: d.location, broker: d.broker, askingPrice: d.askingPrice, ebitda: d.ebitda, revenue: d.revenue })} title="Load into model & run returns" data-testid={`tracker-underwrite-${d.id}`}><Calculator size={13} /></button>
                      <button className="btn" style={{ padding: "3px 6px" }} onClick={() => openContact({ broker: d.broker, company: d.brokerCompany, location: d.location, dealId: d.id })} title="Find broker contact via web search" data-testid={`tracker-find-${d.id}`}><Search size={13} /></button>
                      <button className="btn" style={{ padding: "3px 6px" }} onClick={() => draftNda(d)} title="Send NDA request" data-testid={`tracker-nda-${d.id}`}><Mail size={13} /></button>
                      <button className="btn" style={{ padding: "3px 6px" }} onClick={() => remove(d)} data-testid={`tracker-del-${d.id}`}><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
                {editId === d.id && (
                  <tr data-testid={`tracker-edit-row-${d.id}`}>
                    <td colSpan={8} style={{ background: "var(--bg2)", padding: 14 }}>
                      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text3)", marginBottom: 8 }}>Edit deal — complete or correct any field</div>
                      <div className="grid g3">
                        <div className="field"><label>Deal name *</label><input className="inp" value={editRow.name} onChange={(e) => ef("name", e.target.value)} data-testid={`edit-name-${d.id}`} /></div>
                        <div className="field"><label>Business type</label><input className="inp" value={editRow.businessType} onChange={(e) => ef("businessType", e.target.value)} data-testid={`edit-type-${d.id}`} /></div>
                        <div className="field"><label>Location</label><input className="inp" value={editRow.location} onChange={(e) => ef("location", e.target.value)} data-testid={`edit-location-${d.id}`} /></div>
                        <div className="field"><label>Revenue ($)</label><MoneyInput value={editRow.revenue} onChange={(v) => ef("revenue", v)} testid={`edit-revenue-${d.id}`} /></div>
                        <div className="field"><label>EBITDA/SDE ($)</label><MoneyInput value={editRow.ebitda} onChange={(v) => ef("ebitda", v)} testid={`edit-ebitda-${d.id}`} /></div>
                        <div className="field"><label>Asking price ($)</label><MoneyInput value={editRow.askingPrice} onChange={(v) => ef("askingPrice", v)} testid={`edit-asking-${d.id}`} /></div>
                        <div className="field"><label>Broker</label><input className="inp" value={editRow.broker} onChange={(e) => ef("broker", e.target.value)} data-testid={`edit-broker-${d.id}`} /></div>
                        <div className="field"><label>Brokerage / company</label><input className="inp" value={editRow.brokerCompany} onChange={(e) => ef("brokerCompany", e.target.value)} data-testid={`edit-company-${d.id}`} /></div>
                        <div className="field"><label>Broker email</label><input className="inp" value={editRow.brokerEmail} onChange={(e) => ef("brokerEmail", e.target.value)} data-testid={`edit-broker-email-${d.id}`} /></div>
                        <div className="field"><label>Broker phone</label><input className="inp" value={editRow.brokerPhone} onChange={(e) => ef("brokerPhone", e.target.value)} data-testid={`edit-broker-phone-${d.id}`} /></div>
                        <div className="field"><label>Source</label><input className="inp" value={editRow.source} onChange={(e) => ef("source", e.target.value)} data-testid={`edit-source-${d.id}`} /></div>
                        <div className="field"><label>Listing URL</label><input className="inp" value={editRow.sourceUrl} onChange={(e) => ef("sourceUrl", e.target.value)} data-testid={`edit-url-${d.id}`} /></div>
                      </div>
                      <div className="field"><label>Summary</label><input className="inp" value={editRow.summary} onChange={(e) => ef("summary", e.target.value)} data-testid={`edit-summary-${d.id}`} /></div>
                      <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                        <button className="btn btn-primary" onClick={saveEdit} data-testid={`edit-save-${d.id}`}><Check size={13} style={{ marginRight: 4 }} />Save changes</button>
                        <button className="btn" onClick={cancelEdit} data-testid={`edit-cancel-${d.id}`}><X size={13} style={{ marginRight: 4 }} />Cancel</button>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {saveOpen && (
        <div className="modal-overlay" data-testid="save-modal" onMouseDown={() => setSaveOpen(false)}>
          <div className="modal-card" onMouseDown={(e) => e.stopPropagation()}>
            <div className="modal-title">Name this search</div>
            <div className="modal-sub">Save these filters (sector, markets, size &amp; sources) to re-run in one click.</div>
            <input className="inp" autoFocus value={searchName} onFocus={(e) => e.target.select()} onChange={(e) => setSearchName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") confirmSaveSearch(); if (e.key === "Escape") setSaveOpen(false); }} data-testid="save-name-input" />
            <div className="modal-actions">
              <button className="btn" onClick={() => setSaveOpen(false)} data-testid="save-cancel">Cancel</button>
              <button className="btn btn-primary" onClick={confirmSaveSearch} data-testid="save-confirm"><Save size={13} style={{ marginRight: 4 }} />Save Search</button>
            </div>
          </div>
        </div>
      )}
      <ConfirmModal open={!!confirm} title={confirm?.title} message={confirm?.message} confirmLabel={confirm?.confirmLabel} danger={confirm?.danger} onCancel={() => setConfirm(null)} onConfirm={() => confirm?.onConfirm?.()} testid="confirm-modal" />

      {contactSearch && (
        <div className="modal-overlay" data-testid="contact-search-modal" onMouseDown={() => setContactSearch(null)}>
          <div className="modal-card" style={{ maxWidth: 560, width: "94%" }} onMouseDown={(e) => e.stopPropagation()}>
            <div className="modal-title">Find broker contact</div>
            <div className="modal-sub">Live web research for <b>{contactSearch.broker || "the broker"}</b>{contactSearch.company ? ` at ${contactSearch.company}` : ""}. Every result carries a source link — <b>verify before outreach</b>; nothing is auto-saved.</div>

            <button className="btn btn-primary" style={{ width: "100%", marginBottom: 8 }} onClick={runResearch} disabled={researching} data-testid="contact-research">
              <Sparkles size={13} />{researching ? "Researching the web…" : "Research with AI (live web search)"}
            </button>
            <button className="btn" style={{ width: "100%", marginBottom: 12 }} onClick={runApollo} disabled={apolloBusy} data-testid="contact-apollo">
              <UserSearch size={13} />{apolloBusy ? "Looking up Apollo…" : "Enrich with Apollo (people DB)"}
            </button>

            {apollo && (
              <div className="banner info" style={{ marginBottom: 12 }} data-testid="apollo-result">
                <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{ fontSize: 11, color: "var(--text2)" }}>Apollo match{apollo.title ? ` — ${apollo.title}` : ""}{apollo.organization_name ? ` @ ${apollo.organization_name}` : ""}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                    <Phone size={12} style={{ color: "var(--text3)" }} /><b style={{ minWidth: 46 }}>Phone:</b>
                    <span style={{ flex: 1 }}>{apollo.phone || <span style={{ color: "var(--text3)" }}>locked / none</span>}</span>
                    {apollo.phone && <button className="btn btn-sm" onClick={() => applyContact("brokerPhone", apollo.phone)} data-testid="apollo-use-phone">Use</button>}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                    <Mail size={12} style={{ color: "var(--text3)" }} /><b style={{ minWidth: 46 }}>Email:</b>
                    <span style={{ flex: 1, wordBreak: "break-all" }}>{apollo.email || <span style={{ color: "var(--text3)" }}>locked / none</span>}</span>
                    {apollo.email && <button className="btn btn-sm" onClick={() => applyContact("brokerEmail", apollo.email)} data-testid="apollo-use-email">Use</button>}
                  </div>
                  {apollo.linkedin_url && <div style={{ fontSize: 12 }}><b>LinkedIn: </b><a href={apollo.linkedin_url} target="_blank" rel="noreferrer" style={{ color: "var(--lime)" }}>{apollo.linkedin_url}</a></div>}
                  {!apollo.email && !apollo.phone && <div style={{ fontSize: 11, color: "var(--text3)" }}>Matched the person but email/phone are locked on this Apollo plan (enrichment credits required to reveal).</div>}
                </div>
              </div>
            )}

            {research && (
              <div className="banner info" style={{ marginBottom: 12 }} data-testid="research-result">
                <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 11, color: "var(--text2)" }}>Confidence:</span>
                    <span className={`pill ${research.confidence === "high" ? "go" : research.confidence === "medium" ? "amber" : "pass"}`} style={{ fontSize: 9 }}>{research.confidence}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                    <Phone size={12} style={{ color: "var(--text3)" }} /><b style={{ minWidth: 46 }}>Phone:</b>
                    <span style={{ flex: 1 }}>{research.office_phone || <span style={{ color: "var(--text3)" }}>not publicly found</span>}</span>
                    {research.office_phone && <button className="btn btn-sm" onClick={() => applyContact("brokerPhone", research.office_phone)} data-testid="use-phone">Use</button>}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                    <Mail size={12} style={{ color: "var(--text3)" }} /><b style={{ minWidth: 46 }}>Email:</b>
                    <span style={{ flex: 1, wordBreak: "break-all" }}>{research.business_email || <span style={{ color: "var(--text3)" }}>not publicly found</span>}</span>
                    {research.business_email && <button className="btn btn-sm" onClick={() => applyContact("brokerEmail", research.business_email)} data-testid="use-email">Use</button>}
                  </div>
                  {research.brokerage_profile_url && (
                    <div style={{ fontSize: 12 }}><b>Profile: </b><a href={research.brokerage_profile_url} target="_blank" rel="noreferrer" style={{ color: "var(--lime)" }}>{research.brokerage_profile_url}</a></div>
                  )}
                  {research.notes && <div style={{ fontSize: 11, color: "var(--text3)" }}>{research.notes}</div>}
                  {research.citations?.length > 0 && (
                    <div>
                      <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text2)", marginBottom: 4 }}>Sources</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        {research.citations.map((c, i) => (
                          <a key={i} href={c.url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: "var(--text2)" }} data-testid={`research-cite-${i}`}>{i + 1}. {c.title}</a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text2)", marginBottom: 6 }}>Manual search links</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {brokerSearchLinks(contactSearch.broker, contactSearch.company, contactSearch.location).map((l, i) => (
                <a key={i} className="btn" href={l.url} target="_blank" rel="noreferrer" style={{ justifyContent: "flex-start" }} data-testid={`contact-link-${i}`}><Search size={13} />{l.label}<ExternalLink size={12} style={{ marginLeft: "auto", opacity: 0.6 }} /></a>
              ))}
            </div>
            <div className="modal-actions">
              <button className="btn" onClick={() => setContactSearch(null)} data-testid="contact-close">Close</button>
            </div>
          </div>
        </div>
      )}

      <ResearchModal open={listingResearch.open} onClose={() => listingResearch.setOpen(false)} title="Find real listings & brokers — live web search"
        subtitle="Perplexity searches the web for businesses currently/recently marketed for sale and the brokers marketing them. Save adds them to your Deal Tracker. Marketplace pages are often gated, so results skew to brokerage & announcement pages — verify each."
        kind="listings" query={listingResearch.query} setQuery={listingResearch.setQuery} location={listingResearch.location} setLocation={listingResearch.setLocation}
        run={listingResearch.run} findMore={listingResearch.findMore} loading={listingResearch.loading} result={listingResearch.result} describe={describeListing} onSave={saveListing} />
    </div>
  );
}
